import { isFieldNode, isGroupNode } from '@rules/grammar';
import { DG_SECTION_IDS, type DGSectionId } from '@rules/tree';
import { detailsAndWeightsTree } from './schema';
import type { DgErrorKey, RuleSchema, SchemaFieldNode, SchemaGroupNode } from '@rules/grammar';

export type { DGSectionId };

export function walkSchema<S>(
  tree: RuleSchema<S>,
  onLeaf: (node: SchemaFieldNode<S>) => void
): void {
  function walk(node: SchemaFieldNode<S> | SchemaGroupNode<S>): void {
    if (isFieldNode(node)) return onLeaf(node);
    if (isGroupNode(node)) {
      for (const child of Object.values(node.children)) walk(child);
    }
  }
  for (const node of Object.values(tree)) walk(node);
}

/** First leaf wins when multiple leaves share an errorKey. */
export function getConfigurableFieldsFromTree<S>(
  tree: RuleSchema<S>
): { field: DgErrorKey; label: string; severityLocked: boolean; section: S | undefined }[] {
  const seen = new Map<
    DgErrorKey,
    { label: string; severityLocked: boolean; section: S | undefined }
  >();
  walkSchema(tree, (node) => {
    if (seen.has(node.errorKey)) return;
    seen.set(node.errorKey, {
      label: node.label ?? node.errorKey,
      severityLocked: node.severityLocked === true,
      section: node.section,
    });
  });
  return [...seen].map(([field, v]) => ({ field, ...v }));
}

/** First leaf wins when multiple leaves share an errorKey. */
export function getLabelsFromTree<S>(tree: RuleSchema<S>): Partial<Record<DgErrorKey, string>> {
  const labels: Partial<Record<DgErrorKey, string>> = {};
  walkSchema(tree, (node) => {
    if (node.label && !(node.errorKey in labels)) {
      labels[node.errorKey] = node.label;
    }
  });
  return labels;
}

export function getHashPathsFromTree<S>(tree: RuleSchema<S>): readonly string[] {
  const paths = new Set<string>();
  walkSchema(tree, (node) => {
    if (node.hash === false) return;
    const vp = node.valuePath;
    if (typeof vp === 'string') {
      paths.add(vp);
      return;
    }
    if ('orFallback' in vp) {
      for (const p of vp.orFallback) paths.add(p);
      return;
    }
    // substanceIdValue falls back to ctx.validationResult.selectedSubstanceId
    // (non-cargo path); only the cargo side enters the hash.
    if (vp.named === 'substanceIdValue') paths.add('substanceId');
  });
  return [...paths];
}

/** Groups leaves by section tag, keyed by canonical `errorKey` so entries line up with RowErrors keys. */
export function getSectionFields<S extends string>(tree: RuleSchema<S>): Record<S, Set<string>> {
  const out: Partial<Record<S, Set<string>>> = {};
  walkSchema(tree, (node) => {
    if (node.section) {
      (out[node.section] ??= new Set()).add(node.errorKey);
    }
  });
  return out as Record<S, Set<string>>;
}

/**
 * Fields that gate section visibility but are NOT in the FE rule tree — they emit
 * backend errors landing in `dangerousGoodsErrors`, so error-review visibility must
 * still respect them.
 */
const EXTRA_SECTION_FIELDS: Partial<Record<DGSectionId, readonly DgErrorKey[]>> = {
  classification: ['subsidiaryRisk', 'emsNumber', 'packingGroup'],
  emergencyContact: ['emergencyContactName', 'emergencyContactPhone'],
};

function mergeExtras(
  base: Partial<Record<DGSectionId, Set<string>>>
): Record<DGSectionId, Set<string>> {
  // Every section needs at least an empty set so callers don't null-check.
  const out: Record<DGSectionId, Set<string>> = Object.fromEntries(
    DG_SECTION_IDS.map((s) => [s, base[s] ?? new Set<string>()])
  ) as Record<DGSectionId, Set<string>>;

  for (const [section, extras] of Object.entries(EXTRA_SECTION_FIELDS) as [
    DGSectionId,
    readonly DgErrorKey[],
  ][]) {
    for (const f of extras) out[section].add(f);
  }
  return out;
}

/** Tree-derived + extras. Built once at module load — tree is static. */
export const SECTION_FIELDS: Record<DGSectionId, Set<string>> = mergeExtras(
  getSectionFields(detailsAndWeightsTree)
);
